import axios from 'axios';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { sentryService } from '@/services/index';

const BASE_URL = `${process.env.REACT_APP_ENDPOINT}/sitemap`;

// Type definitions
interface BreadcrumbItem {
  '@type': string;
  position: number;
  name: string;
  item: string;
}

interface BreadcrumbSchema {
  '@context': string;
  '@type': string;
  itemListElement: BreadcrumbItem[];
}

interface SeoData {
  seoTitle: string;
  description: string;
  keywords?: string[];
  urlSlug?: string;
  image?: string;
  type?: string;
  locale?: string;
  twitterHandle?: string;
}

interface ApiResponse<T> {
  msg: string;
  data: T;
}

interface JsonLdData {
  [key: string]: any;
}

interface SeoHelmetProps {
  data: SeoData;
}

const SeoHelmet: React.FC<SeoHelmetProps> = ({ data }) => {
  const [jsonLd, setJsonLd] = useState<JsonLdData | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbSchema | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchSchemaData = async (): Promise<void> => {
      setLoading(true);
      try {
        // Make parallel API calls using axios
        const [jsonLdAxiosResponse, breadcrumbAxiosResponse] = await Promise.all([
          axios.get<ApiResponse<string | JsonLdData>>(`${BASE_URL}/getJSONLD`),
          axios.get<ApiResponse<BreadcrumbSchema>>(`${BASE_URL}/getBreadcrumbSchema`),
        ]);

        const jsonLdResponse = jsonLdAxiosResponse.data;
        const breadcrumbResponse = breadcrumbAxiosResponse.data;

        //console.log('JSON-LD Response:', jsonLdResponse);
        //console.log('Breadcrumb Response:', breadcrumbResponse);

        // Process JSON-LD data
        if (jsonLdResponse.msg === 'success' && jsonLdResponse.data) {
          const jsonLdData = jsonLdResponse.data;

          // Check if data is a string that needs parsing, or already an object
          const parsedJsonLd: JsonLdData =
            typeof jsonLdData === 'string' ? JSON.parse(jsonLdData) : jsonLdData;

          setJsonLd(parsedJsonLd);
        }

        // Process Breadcrumb data
        if (
          breadcrumbResponse?.msg === 'success' &&
          breadcrumbResponse.data?.itemListElement?.length > 0
        ) {
          const breadcrumbData: BreadcrumbSchema = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbResponse.data.itemListElement,
          };
          setBreadcrumb(breadcrumbData);
        }
      } catch (error) {
        const extra =
          axios.isAxiosError(error) && error.response
            ? {
                status: error.response.status,
                data: error.response.data,
              }
            : undefined;
        sentryService.captureException(error, extra ? { extra } : undefined);
      } finally {
        setLoading(false);
      }
    };

    fetchSchemaData();
  }, [data.urlSlug]);

  // Construct canonical and full URLs
  const canonicalUrl: string = `${window.location.origin}${window.location.pathname}`;
  const fullUrl: string = `${window.location.origin}${data.urlSlug || window.location.pathname}`;

  return (
    <Helmet>
      {/* HTML Lang */}
      <html lang={data.locale || 'en-GB'} />

      {/* Primary Meta Tags */}
      <title>{data.seoTitle}</title>
      <meta name="title" content={data.seoTitle} />
      <meta name="description" content={data.description} />
      {Array.isArray(data.keywords) && data.keywords.length > 0 && (
        <meta name="keywords" content={data.keywords.join(', ')} />
      )}

      {/* Technical Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={data.locale || 'en-GB'} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={data.type || 'website'} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={data.seoTitle} />
      <meta property="og:description" content={data.description} />
      <meta property="og:locale" content={(data.locale || 'en-GB').replace('-', '_')} />
      {data.image ? <meta property="og:image" content={data.image} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={data.seoTitle} />
      <meta name="twitter:description" content={data.description} />
      {data.image ? <meta name="twitter:image" content={data.image} /> : null}
      {data.twitterHandle ? <meta name="twitter:site" content={data.twitterHandle} /> : null}

      {/* Structured Data Scripts */}
      {!loading && jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
      {!loading && breadcrumb ? (
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      ) : null}
    </Helmet>
  );
};

export default SeoHelmet;

// Export types for use in other files
export type { SeoData, BreadcrumbSchema, BreadcrumbItem, SeoHelmetProps };
